import { reactive } from 'vue'
import { useMetadataListasStore } from '@/stores/metadataListas'

/**
 * Composable de resolução de campos de lista (Lst*) para texto amigável.
 *
 * Padrão do projeto (extraído do ModalDeBusca): para cada campo cujo nome segue
 * o padrão `Lst*` (ex: `lstMoeda`, `lstStatusPedido`), resolve o valor bruto
 * (idListaClienteItem) para o texto amigável via Dicionário de Metadados
 * (`metadataStore.getListaPorCampo`) e injeta o campo `${campo}Texto` no item.
 *
 * Utilizado pelo AdminTable (prop `resolverListasTabela`) e pelo ModalDeBusca,
 * garantindo a troca de código → texto em TODAS as telas que adotarem o recurso.
 *
 * @example
 * const { resolverListasEmLote, campoResolvido } = useResolverListas()
 * await resolverListasEmLote(itens, 'PedidoVenda')
 * const textoMoeda = campoResolvido(item, 'lstMoeda') // ou item.lstMoedaTexto
 */
export function useResolverListas () {
  const metadataStore = useMetadataListasStore()

  // Cache persistente entre chamadas (nível de módulo) para não re-buscar listas repetidas.
  const listasCache = reactive({})

  /**
   * Verifica se um campo é do tipo lista (padrão Lst*).
   * @param {string} campo
   * @returns {boolean}
   */
  const isCampoLst = (campo) => /^[lL]st[A-Z]/.test(campo)

  /**
   * Busca (com cache) o texto amigável de um valor de lista para um campo.
   * @param {string} tabela Nome da tabela no banco
   * @param {string} campo Nome do campo Lst*
   * @param {*} valor Valor bruto (idListaClienteItem)
   * @returns {Promise<string|null>}
   */
  const obterTexto = async (tabela, campo, valor) => {
    if (valor === null || valor === undefined || valor === '') return null
    const cacheKey = `${tabela}.${campo}`
    if (!listasCache[cacheKey]) {
      try {
        listasCache[cacheKey] = (await metadataStore.getListaPorCampo(tabela, campo)) || []
      } catch (e) {
        console.error(`[useResolverListas] Erro ao carregar lista ${cacheKey}:`, e)
        listasCache[cacheKey] = []
      }
    }
    const lista = listasCache[cacheKey]
    const item = (lista || []).find(i =>
      String(i.idListaClienteItem) === String(valor) ||
      String(i.idListaSistemaItem) === String(valor)
    )
    return item ? item.textoLista : null
  }

  /**
   * Resolve campos Lst* em lote para um array de itens.
   * Injeta `{campo}Texto` com o `textoLista` resolvido para exibição no grid.
   * Aceita uma única tabela (string) ou múltiplas tabelas (array). Quando o
   * array é informado, tenta cada tabela em sequência até encontrar o texto
   * amigável (fallback entre tabelas, ex: ['ReservaEstoque', 'ReservaEstoqueItem']).
   * @param {Array<object>} itens
   * @param {string|string[]} tabelas Nome(s) da(s) tabela(s) no Dicionário de Metadados
   * @returns {Promise<void>}
   */
  const resolverListasEmLote = async (itens, tabelas) => {
    const listaTabelas = Array.isArray(tabelas) ? tabelas : [tabelas]
    if (!listaTabelas.length || !Array.isArray(itens) || itens.length === 0) return

    await metadataStore.carregarMetadados()

    for (const item of itens) {
      if (!item || typeof item !== 'object') continue

      const chavesLst = Object.keys(item).filter(isCampoLst)

      for (const key of chavesLst) {
        const valor = item[key]
        if (valor === null || valor === undefined || valor === '') continue
        if (item[`${key}Texto`]) continue

        // Tenta cada tabela até encontrar o texto amigável (fallback sequencial)
        for (const tabela of listaTabelas) {
          const texto = await obterTexto(tabela, key, valor)
          if (texto) {
            item[`${key}Texto`] = texto
            break
          }
        }
      }
    }
  }

  /**
   * Devolve o valor de exibição de um campo de lista: o texto amigável
   * (`${campo}Texto`) quando disponível; caso contrário, o valor bruto.
   * @param {object} row Item da linha
   * @param {string} campo Nome do campo (ex: lstMoeda)
   * @returns {*}
   */
  const campoResolvido = (row, campo) => {
    if (!row || campo == null) return undefined
    if (isCampoLst(campo) && row[`${campo}Texto`] !== undefined) {
      return row[`${campo}Texto`]
    }
    return row[campo]
  }

  return {
    isCampoLst,
    obterTexto,
    resolverListasEmLote,
    campoResolvido
  }
}
